import React, { useEffect, useState, useMemo, useRef } from 'react';
import './App.css';

const MAX_PROMPT_LENGTH = 2000;
const MAX_NAME_LENGTH = 100;

const FEATURE_SLIDES = [
  {
    id: 'ai-predict',
    type: 'text',
    headline: 'Use AI (LLM models) to predict audience reactions to your ideas with demographic insights.'
  },
  {
    id: 'support',
    type: 'text',
    headline: 'Simply create a poll here then request AI to simulate votes for you instantly and cheaply.'
  },
  {
    id: 'audience',
    type: 'text',
    headline: 'Want to know if your audience will love or hate your planning speeches? Ask AI.'
  },
  {
    id: 'visual',
    type: 'image',
    src: `${process.env.PUBLIC_URL}/feature-slide.png`,
    alt: 'Polling simulator preview card'
  },
  {
    id: 'visual-2',
    type: 'image',
    src: `${process.env.PUBLIC_URL}/feature-slide2.png`,
    alt: 'Setting audience preview card'
  }
];

const BRAND_ICONS = [
  {
    id: 'producthunt',
    label: 'Poll Sim on Product Hunt',
    icon: 'productHuntBadge',
    href: 'https://www.producthunt.com/products/poll-sim?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-poll-sim',
    target: '_blank',
    rel: 'noopener noreferrer'
  },
  { id: 'discord', label: 'Discord', icon: 'mdiDiscord', href: 'https://discord.gg/wuFZ2bE4mQ' },
  { id: 'email', label: 'Email', icon: 'mdiEmail', href: 'mailto:pollsim@proton.me' },
  { id: 'help', label: 'Help', icon: 'mdiHelpCircle', href: '/help' },
  {
    id: 'shipit',
    label: 'Featured on Shipit',
    icon: 'shipitBadge',
    href: 'https://www.shipit.buzz/products/poll-simulator?ref=badge',
    target: '_blank',
    rel: 'noopener noreferrer'
  }
];

/*
  {
    id: 'aiDir',
    label: 'Featured on AI Agents Directory',
    icon: 'aiDirBadge',
    href: 'https://aiagentsdirectory.com/agent/poll-sim?utm_source=badge&utm_medium=referral&utm_campaign=free_listing&utm_content=poll-sim',
    target: '_blank',
    rel: 'noopener noreferrer'
  }
*/

//{ id: 'facebook', label: 'Facebook', icon: 'mdiFacebook', href: 'https://www.facebook.com/profile.php?id=61568022365913' },
//{ id: 'bluesky', label: 'Bluesky', icon: 'mdiBluesky', href: 'https://bsky.app/profile/poll-sim.bsky.social' },
//  { id: 'x', label: 'X', icon: 'mdiX', href: 'https://x.com/pollsimulator' },
   

const POLL_ACTION_PRESETS = [
  'Announces a new healthcare subsidy plan for low-income families.',
  'Delivers a speech on improving public safety through community policing.',
  'Proposes a tax reduction package for small businesses.',
  'Publishes a statement supporting stricter environmental regulations.',
  'Unveils a housing affordability program for first-time buyers.',
  'Commits to increased education funding for public schools.',
  'Announces a transportation upgrade plan for major cities.',
  'Issues a statement on protecting digital privacy rights.',
  'Launches a citywide cleanup drive with local volunteers.',
  'Announces a public campaign to support mental health awareness.',
  'Introduces a scholarship fund for underrepresented students.',
  'Reveals a new company policy for flexible remote work.',
  'Opens a free weekend coding bootcamp for teenagers.',
  'Publishes a plan to reduce product prices for essential goods.',
  'Sponsors a local sports event to encourage youth participation.',
  'Announces a new customer loyalty reward program.',
  'Commits to making all packaging recyclable within two years.',
  'Introduces AI-powered tutoring in selected schools.',
  'Launches a neighborhood safety app with emergency alerts.',
  'Partners with nonprofits to expand food bank access.',
  'Announces a major investment in renewable energy projects.',
  'Rolls out free Wi-Fi in libraries and community centers.',
  'Commits to publishing transparent quarterly progress reports.'
];

const PRESET_SAMPLE_SIZE = 5;

const getRandomPollActionPresets = (presets, size) => {
  const shuffled = [...presets].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(size, presets.length));
};

const getShareTokenFromLocation = () => {
  const pathMatch = window.location.pathname.match(/^\/share\/([0-9a-fA-F-]{36})$/);
  if (pathMatch?.[1]) {
    return pathMatch[1];
  }
  const params = new URLSearchParams(window.location.search || '');
  const queryToken = params.get('share');
  if (queryToken && /^[0-9a-fA-F-]{36}$/.test(queryToken)) {
    return queryToken;
  }
  return null;
};

const POLL_CREATED_ALERT_COOKIE = 'pollCreatedAlertAt';
const POLL_SIMULATION_ALERT_COOKIE = 'pollSimulationAlertAt';
const POLL_CREATED_ALERT_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const POLL_SIMULATION_ALERT_COOLDOWN_MS = 6 * 60 * 60 * 1000;

const getCookieValue = (name) => {
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  const prefix = `${name}=`;
  const match = cookies.find((cookie) => cookie.startsWith(prefix));
  if (!match) return null;
  return decodeURIComponent(match.slice(prefix.length));
};

const setCookieValue = (name, value, maxAgeSeconds) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax`;
};

const clearCookieValue = (name) => {
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
};

const shouldShowAlertWithCooldown = (cookieName, cooldownMs) => {
  const lastSeen = Number(getCookieValue(cookieName));
  if (!Number.isFinite(lastSeen)) {
    return true;
  }
  return Date.now() - lastSeen >= cooldownMs;
};

const markAlertShown = (cookieName, cooldownMs) => {
  const maxAgeSeconds = Math.ceil(cooldownMs / 1000);
  setCookieValue(cookieName, String(Date.now()), maxAgeSeconds);
};

const ToggleChevronIcon = () => (
  <svg className="toggle-chevron-icon" viewBox="0 0 24 24" role="img" aria-hidden="true">
    <path d="M4 9 L12 15 L20 9" />
  </svg>
);

const formatPollCreatedDate = (value) => {
  const parsedDate = value ? new Date(value) : new Date();
  const safeDate = Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
  return safeDate.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

function App() {
  const [personas, setPersonas] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [groups, setGroups] = useState([]);
  const [polls, setPolls] = useState([]);
  const [newlyCreatedPollId, setNewlyCreatedPollId] = useState(null);
  const [pollExpandRequest, setPollExpandRequest] = useState({ pollId: null, nonce: 0 });
  const [pollResults, setPollResults] = useState({});
  const [voteDetails, setVoteDetails] = useState({});
  const [voteDetailsPollId, setVoteDetailsPollId] = useState(null);
  const [simulatingPollId, setSimulatingPollId] = useState(null);
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);

  const [personaName, setPersonaName] = useState('');
  const [personaPrompt, setPersonaPrompt] = useState('');
  const [audienceName, setAudienceName] = useState('');
  const [audiencePrompt, setAudiencePrompt] = useState('');
  const [audiencePublic, setAudiencePublic] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupPrompt, setGroupPrompt] = useState('');
  const [groupPublic, setGroupPublic] = useState(false);
  const [pollFormOpen, setPollFormOpen] = useState(false);
  const [pollPersona, setPollPersona] = useState('');
  const [pollAudience, setPollAudience] = useState('');
  const [pollAction, setPollAction] = useState('');
  const [pollActionPreset, setPollActionPreset] = useState('');
  const [pollActionPresetOptions, setPollActionPresetOptions] = useState(() =>
    getRandomPollActionPresets(POLL_ACTION_PRESETS, PRESET_SAMPLE_SIZE)
  );

  const [showOtherPollTypes, setShowOtherPollTypes] = useState(false);
  const [opinionsPollFormOpen, setOpinionsPollFormOpen] = useState(false);
  const [opinionsPollAudience, setOpinionsPollAudience] = useState('');
  const [opinionsPollQuestion, setOpinionsPollQuestion] = useState('');
  const [opinionsPollOptions, setOpinionsPollOptions] = useState(['', '']);

  const [personaFormOpen, setPersonaFormOpen] = useState(false);
  const [audienceFormOpen, setAudienceFormOpen] = useState(false);
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAiHelpBanner, setShowAiHelpBanner] = useState(true);

  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [initializingUser, setInitializingUser] = useState(true);

  const API_BASE = '/api'; //process.env.REACT_APP_API_BASE || 'https://poll-sim-api.azurewebsites.net';
  const sharedPollToken = useMemo(() => getShareTokenFromLocation(), []);
  const isPublicShareView = Boolean(sharedPollToken);
  const [publicPoll, setPublicPoll] = useState(null);
  const [publicPollLoading, setPublicPollLoading] = useState(false);
  const [publicPollError, setPublicPollError] = useState('');
  const [dashboardStats, setDashboardStats] = useState({
    loading: false,
    error: null,
    newUsers: 0,
    newPolls: 0,
    newVotes: 0,
    newShares: 0
  });
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const isAdmin = user?.userType === 'admin';
  const [stripeConfig, setStripeConfig] = useState({ enabled: false, loading: false, error: null });
  const [simulationProgress, setSimulationProgress] = useState({});
  const simulationTimers = useRef({});
  const SIMULATION_STEPS = 10;

  useEffect(() => {
    if (isPublicShareView) {
      return;
    }
    if (!token) {
      setStripeConfig({ enabled: false, loading: false, error: null });
      return;
    }

    let cancelled = false;
    const loadStripeConfig = async () => {
      setStripeConfig((prev) => ({ ...prev, loading: true }));
      try {
        const res = await fetch(`${API_BASE}/api/stripe/config`, { headers: authHeaders });
        if (!res.ok) {
          throw new Error('Unable to load Stripe configuration');
        }
        const data = await res.json();
        if (!cancelled) {
          setStripeConfig({
            enabled: Boolean(data?.publishableKey && data?.buyButtonId),
            loading: false,
            error: null,
            publishableKey: data.publishableKey,
            buyButtonId: data.buyButtonId
          });
        }
      } catch (err) {
        console.error('Failed to load Stripe config', err);
        if (!cancelled) {
          setStripeConfig({ enabled: false, loading: false, error: 'Stripe unavailable' });
        }
      }
    };

    loadStripeConfig();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, authHeaders, token, isPublicShareView]);

  useEffect(() => {
    if (isPublicShareView) {
      return;
    }
    if (!token) {
      setInitializingUser(false);
      return;
    }

    const loadProfile = async () => {
      setInitializingUser(true);
      try {
        const res = await fetch(`${API_BASE}/api/auth/me`, { headers: authHeaders });
        if (!res.ok) {
          throw new Error('Failed to load profile');
        }
        const profile = await res.json();
        setUser((prev) => {
          const merged = { ...(prev || {}), ...profile };
          localStorage.setItem('user', JSON.stringify(merged));
          return merged;
        });
        setError('');
      } catch (err) {
        console.error(err);
        setError('Failed to refresh user profile');
      } finally {
        setInitializingUser(false);
      }
    };

    loadProfile();
  }, [API_BASE, authHeaders, token, isPublicShareView]);

  useEffect(() => {
    if (isPublicShareView) {
      return;
    }
    if (!token) return;

    const fetchData = async () => {
      try {
        const [personaData, audienceData, groupData, pollData] = await Promise.all([
          fetch(`${API_BASE}/api/personas`, { headers: authHeaders }).then((res) => {
            if (!res.ok) throw new Error();
            return res.json();
          }),
          fetch(`${API_BASE}/api/audiences`, { headers: authHeaders }).then((res) => {
            if (!res.ok) throw new Error();
            return res.json();
          }),
          fetch(`${API_BASE}/api/groups`, { headers: authHeaders }).then((res) => {
            if (!res.ok) throw new Error();
            return res.json();
          }),
          fetch(`${API_BASE}/api/polls`, { headers: authHeaders }).then((res) => {
            if (!res.ok) throw new Error();
            return res.json();
          })
        ]);
        setPersonas(personaData);
        setAudiences(audienceData);
        setGroups(groupData);
        setPolls(pollData);
        setError('');
      } catch (err) {
        console.error(err);
        setError('Failed to load workspace data');
      }
    };

    fetchData();
  }, [API_BASE, authHeaders, token, isPublicShareView]);

  useEffect(() => {
    if (!isPublicShareView || !sharedPollToken) {
      return;
    }

    let cancelled = false;
    const fetchPublicPoll = async () => {
      setPublicPollLoading(true);
      setPublicPollError('');
      try {
        const res = await fetch(`${API_BASE}/api/public/polls/${encodeURIComponent(sharedPollToken)}`);
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.message || 'Poll not found');
        }
        if (!cancelled) {
          setPublicPoll(data);
        }
      } catch (err) {
        if (!cancelled) {
          setPublicPoll(null);
          setPublicPollError(err.message || 'Failed to load shared poll');
        }
      } finally {
        if (!cancelled) {
          setPublicPollLoading(false);
        }
      }
    };

    fetchPublicPoll();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, isPublicShareView, sharedPollToken]);

  useEffect(() => {
    if (isPublicShareView || !token || !isAdmin) {
      setDashboardStats({
        loading: false,
        error: null,
        newUsers: 0,
        newPolls: 0,
        newVotes: 0,
        newShares: 0
      });
      return;
    }

    let cancelled = false;
    const fetchDashboardStats = async () => {
      setDashboardStats((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const res = await fetch(`${API_BASE}/api/admin/dashboard/new-activity`, { headers: authHeaders });
        const rawBody = await res.text();
        let data = null;
        try {
          data = rawBody ? JSON.parse(rawBody) : null;
        } catch (_parseError) {
          data = null;
        }
        if (!res.ok) {
          throw new Error(data?.message || `Failed to load dashboard metrics (HTTP ${res.status})`);
        }
        if (!data || typeof data !== 'object') {
          throw new Error('Dashboard metrics endpoint returned an invalid response');
        }
        if (!cancelled) {
          setDashboardStats({
            loading: false,
            error: null,
            newUsers: Number(data?.newUsers || 0),
            newPolls: Number(data?.newPolls || 0),
            newVotes: Number(data?.newVotes || 0),
            newShares: Number(data?.newShares || 0)
          });
        }
      } catch (err) {
        if (!cancelled) {
          setDashboardStats((prev) => ({
            ...prev,
            loading: false,
            error: err.message || 'Failed to load dashboard metrics'
          }));
        }
      }
    };

    fetchDashboardStats();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, authHeaders, token, isAdmin, isPublicShareView]);

  useEffect(() => {
    if (pollFormOpen) {
      setPollActionPresetOptions(getRandomPollActionPresets(POLL_ACTION_PRESETS, PRESET_SAMPLE_SIZE));
      setPollActionPreset('');
    }
  }, [pollFormOpen]);

  useEffect(() => {
    if (newlyCreatedPollId == null) {
      return;
    }
    if (polls.some((poll) => poll.id === newlyCreatedPollId)) {
      setNewlyCreatedPollId(null);
    }
  }, [polls, newlyCreatedPollId]);

  const handleCreatePersona = (e) => {
    e.preventDefault();
    if (!personaName.trim() || !personaPrompt.trim()) {
      setError('Public Figure name and description are required.');
      return;
    }

    fetch(`${API_BASE}/api/personas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ personaName, aiPrompt: personaPrompt })
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((persona) => {
        setPersonas((prev) => [...prev, persona]);
        setPersonaName('');
        setPersonaPrompt('');
        setError('');
      })
      .catch(() => setError('Failed to create public figure'));
  };

  const handleCreateAudience = (e) => {
    e.preventDefault();
    if (!audienceName.trim() || !audiencePrompt.trim()) {
      setError('Audience name and description are required.');
      return;
    }

    fetch(`${API_BASE}/api/audiences`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        name: audienceName,
        aiPrompt: audiencePrompt,
        isPublic: isAdmin && audiencePublic
      })
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((audience) => {
        setAudiences((prev) => [...prev, audience]);
        setAudienceName('');
        setAudiencePrompt('');
        setAudiencePublic(false);
        setError('');
      })
      .catch(() => setError('Failed to create audience'));
  };

  const handleCreateGroup = (e) => {
    e.preventDefault();
    if (!groupName.trim() || !groupPrompt.trim()) {
      setError('Group name and description are required.');
      return;
    }

    fetch(`${API_BASE}/api/groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ name: groupName, aiPrompt: groupPrompt, isPublic: isAdmin && groupPublic })
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((group) => {
        setGroups((prev) => [...prev, group]);
        setGroupName('');
        setGroupPrompt('');
        setGroupPublic(false);
        setError('');
      })
      .catch(() => setError('Failed to create group'));
  };

  const updatePersonaPrompt = (id, aiPrompt) => {
    return fetch(`${API_BASE}/api/personas/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ aiPrompt })
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        setPersonas((prev) => prev.map((persona) => (persona.id === id ? { ...persona, aiPrompt } : persona)));
      });
  };

  const archivePersona = (id) => {
    return fetch(`${API_BASE}/api/personas/${id}/archive`, {
      method: 'PATCH',
      headers: { ...authHeaders }
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        setPersonas((prev) => prev.filter((persona) => persona.id !== id));
      });
  };

  const updateAudiencePrompt = (id, aiPrompt) => {
    return fetch(`${API_BASE}/api/audiences/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ aiPrompt })
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        setAudiences((prev) => prev.map((audience) => (audience.id === id ? { ...audience, aiPrompt } : audience)));
      });
  };

  const archiveAudience = (id) => {
    return fetch(`${API_BASE}/api/audiences/${id}/archive`, {
      method: 'PATCH',
      headers: { ...authHeaders }
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        setAudiences((prev) => prev.filter((audience) => audience.id !== id));
      });
  };

  const updateGroupPrompt = (id, aiPrompt) => {
    return fetch(`${API_BASE}/api/groups/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ aiPrompt })
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        setGroups((prev) => prev.map((group) => (group.id === id ? { ...group, aiPrompt } : group)));
      });
  };

  const archiveGroup = (id) => {
    return fetch(`${API_BASE}/api/groups/${id}/archive`, {
      method: 'PATCH',
      headers: { ...authHeaders }
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        setGroups((prev) => prev.filter((group) => group.id !== id));
      });
  };

  const requestCode = (e) => {
    e.preventDefault();
    fetch(`${API_BASE}/api/auth/request-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        setStep('code');
        setError('');
      })
      .catch(() => setError('Failed to send verification code'));
  };

  const verifyCode = (e) => {
    e.preventDefault();
    fetch(`${API_BASE}/api/auth/verify-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code })
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.token) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          setToken(data.token);
          setUser(data.user);
          setPersonas(data.user.personas || []);
          setAudiences([]);
          setGroups([]);
          setPolls([]);
          setPollResults({});
          setError('');
        } else {
          throw new Error();
        }
      })
      .catch(() => setError('Invalid code'));
  };

  const signOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearCookieValue(POLL_CREATED_ALERT_COOKIE);
    clearCookieValue(POLL_SIMULATION_ALERT_COOKIE);
    setToken(null);
    setUser(null);
    setPersonas([]);
    setAudiences([]);
    setGroups([]);
    setPolls([]);
    setPollResults({});
    setVoteDetails({});
    setVoteDetailsPollId(null);
  };

  const fetchAudienceGroups = (audienceId) => {
    return fetch(`${API_BASE}/api/audiences/${audienceId}/groups`, {
      headers: authHeaders
    }).then((res) => {
      if (!res.ok) throw new Error();
      return res.json();
    });
  };

  const handleToggleOptions = async (pollId, nextOpen) => {
    if (nextOpen && !pollResults[pollId]) {
      try {
        await fetchPollResults(pollId);
      } catch (err) {
        console.error('Failed to fetch poll results', err);
      }
    }
  };

  const linkGroupToAudience = (audienceId, payload) => {
    return fetch(`${API_BASE}/api/audiences/${audienceId}/groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify(payload)
    }).then((res) => {
      if (!res.ok) throw new Error();
      return res.json();
    });
  };

  const unlinkGroupFromAudience = (audienceId, groupId) => {
    return fetch(`${API_BASE}/api/audiences/${audienceId}/groups/${groupId}`, {
      method: 'DELETE',
      headers: authHeaders
    }).then((res) => {
      if (!res.ok) throw new Error();
      return res.json();
    });
  };

  const handleCreatePoll = (e) => {
    e.preventDefault();
    if (!pollPersona || !pollAudience || !pollAction.trim()) {
      setError('Select a public figure, audience, and describe the action.');
      return;
    }

    createScenarioPoll(Number(pollPersona), Number(pollAudience), pollAction.trim());
  };

  const createScenarioPoll = (personaId, audienceId, actionStatement) => {
    return fetch(`${API_BASE}/api/polls`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ personaId, audienceId, actionStatement })
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((poll) => {
        setPolls((prev) => [poll, ...prev]);
        setNewlyCreatedPollId(poll.id);
        setPollPersona('');
        setPollAudience('');
        setPollAction('');
        setPollActionPreset('');
        setPollFormOpen(false);
        setError('');
        if (shouldShowAlertWithCooldown(POLL_CREATED_ALERT_COOKIE, POLL_CREATED_ALERT_COOLDOWN_MS)) {
          setInfoMessage('Poll created. Press "AI to Simulate" button to get 10 votes.');
          markAlertShown(POLL_CREATED_ALERT_COOKIE, POLL_CREATED_ALERT_COOLDOWN_MS);
        }
      })
      .catch(() => setError('Failed to create poll'));
  };

  const handlePollActionPresetSelect = (e) => {
    const selectedPreset = e.target.value;
    setPollActionPreset('');
    if (!selectedPreset) {
      return;
    }
    if (!pollAction.trim()) {
      setPollAction(selectedPreset);
    }
  };

  const MAX_OPINIONS_OPTIONS = 10;

  const handlePrefillFirstTimeScenario = () => {
    const availablePersonas = personas.filter((p) => !p.archived);
    const availableAudiences = audiences.filter((a) => !a.archived);
    const availablePresets = pollActionPresetOptions.length > 0 ? pollActionPresetOptions : POLL_ACTION_PRESETS;

    if (!availablePersonas.length || !availableAudiences.length || !availablePresets.length) {
      setError('Need at least one public figure, one audience, and one preset action.');
      return;
    }

    const randomPersona = availablePersonas[Math.floor(Math.random() * availablePersonas.length)];
    const randomAudience = availableAudiences[Math.floor(Math.random() * availableAudiences.length)];
    const randomPreset = availablePresets[Math.floor(Math.random() * availablePresets.length)];

    setPollPersona(String(randomPersona.id));
    setPollAudience(String(randomAudience.id));
    setPollActionPreset(randomPreset);
    setPollAction(randomPreset);
    setPollFormOpen(false);

    createScenarioPoll(Number(randomPersona.id), Number(randomAudience.id), randomPreset);
  };

  const handleCreateOpinionsPoll = (e) => {
    e.preventDefault();
    const trimmedOptions = opinionsPollOptions.map((o) => o.trim()).filter(Boolean);
    if (!opinionsPollAudience || !opinionsPollQuestion.trim()) {
      setError('Select an audience and enter a question.');
      return;
    }
    if (trimmedOptions.length < 2) {
      setError('Add at least 2 options.');
      return;
    }

    fetch(`${API_BASE}/api/polls`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({
        audienceId: Number(opinionsPollAudience),
        question: opinionsPollQuestion.trim(),
        options: trimmedOptions
      })
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || 'Failed to create poll');
        return data;
      })
      .then((poll) => {
        setPolls((prev) => [poll, ...prev]);
        setNewlyCreatedPollId(poll.id);
        setOpinionsPollAudience('');
        setOpinionsPollQuestion('');
        setOpinionsPollOptions(['', '']);
        setOpinionsPollFormOpen(false);
        setError('');
      })
      .catch((err) => setError(err.message || 'Failed to create poll'));
  };

  const archivePoll = (id) => {
    return fetch(`${API_BASE}/api/polls/${id}/archive`, {
      method: 'PATCH',
      headers: { ...authHeaders }
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(() => {
        setPolls((prev) => prev.filter((poll) => poll.id !== id));
      });
  };

  const fetchPollResults = async (pollId) => {
    const res = await fetch(`${API_BASE}/api/polls/${pollId}/results`, { headers: authHeaders });
    if (!res.ok) throw new Error('Failed to load poll results');
    const data = await res.json();
    const map = data.reduce(
      (acc, row) => ({
        ...acc,
        [row.optionId]: Number(row.votes)
      }),
      {}
    );
    setPollResults((prev) => ({ ...prev, [pollId]: map }));
    return map;
  };

  const handleSimulatePoll = async (poll) => {
    if (simulatingPollId) return;
    setPollExpandRequest({ pollId: poll.id, nonce: Date.now() });
    setSimulatingPollId(poll.id);
    startSimulationProgress(poll.id);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/polls/${poll.id}/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders }
      });
      let payload = null;
      try {
        payload = await res.json();
      } catch (_err) {
        payload = null;
      }
      if (!res.ok) {
        const message = payload?.message || 'AI simulation failed. Please try again later.';
        if (res.status === 402) {
          setError(message || 'You do not have enough credits.');
        } else {
          setError(message);
        }
        return;
      }
      if (payload && typeof payload.credits === 'number') {
        setUser((prev) => {
          if (!prev) return prev;
          const updated = { ...prev, credits: payload.credits };
          localStorage.setItem('user', JSON.stringify(updated));
          return updated;
        });
      }
      await fetchPollResults(poll.id);
      delete voteDetails[poll.id];
      setVoteDetails((prev) => ({ ...prev, [poll.id]: undefined }));
      if (shouldShowAlertWithCooldown(POLL_SIMULATION_ALERT_COOKIE, POLL_SIMULATION_ALERT_COOLDOWN_MS)) {
        setInfoMessage('Simulation done. Press again for more votes.');
        markAlertShown(POLL_SIMULATION_ALERT_COOKIE, POLL_SIMULATION_ALERT_COOLDOWN_MS);
      }
    } catch (err) {
      console.error(err);
      setError('AI simulation failed. Please try again later.');
    } finally {
      stopSimulationProgress(poll.id);
      setSimulatingPollId(null);
    }
  };

  const startSimulationProgress = (pollId) => {
    setSimulationProgress((prev) => ({ ...prev, [pollId]: 0 }));
    const intervalId = setInterval(() => {
      setSimulationProgress((prev) => {
        const current = prev[pollId] ?? 0;
        if (current >= SIMULATION_STEPS) {
          clearInterval(simulationTimers.current[pollId]);
          delete simulationTimers.current[pollId];
          return { ...prev, [pollId]: SIMULATION_STEPS };
        }
        return { ...prev, [pollId]: current + 1 };
      });
    }, 4000);
    simulationTimers.current[pollId] = intervalId;
  };

  const stopSimulationProgress = (pollId) => {
    if (simulationTimers.current[pollId]) {
      clearInterval(simulationTimers.current[pollId]);
      delete simulationTimers.current[pollId];
    }
    setSimulationProgress((prev) => {
      if (prev[pollId] == null) return prev;
      const { [pollId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  useEffect(() => {
    return () => {
      Object.values(simulationTimers.current).forEach((timerId) => clearInterval(timerId));
      simulationTimers.current = {};
    };
  }, []);

  const fetchVoteDetails = async (pollId) => {
    const res = await fetch(`${API_BASE}/api/polls/${pollId}/votes/detail`, { headers: authHeaders });
    if (!res.ok) throw new Error('Failed to load vote details');
    const data = await res.json();
    setVoteDetails((prev) => ({ ...prev, [pollId]: data }));
    return data;
  };

  const handleTopUpFree = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/api/credits/top-up/free`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders }
      });
      const data = await res.json();
      if (!res.ok) {
        const message = data?.message || 'Unable to top up for free.';
        setError(message);
        if (res.status === 429) {
          setTopUpModalOpen(false);
        }
        return;
      }
      setUser((prev) => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          credits: data.credits,
          lastFreeTopUpAt: data.lastFreeTopUpAt
        };
        localStorage.setItem('user', JSON.stringify(updated));
        return updated;
      });
      setError('');
      setTopUpModalOpen(false);
    } catch (err) {
      console.error('Free top-up failed', err);
      setError('Free top-up failed. Please try again later.');
    }
  };

  const isLoading = initializingUser && token;

  if (isPublicShareView) {
    return (
      <div className="App">
        <Header />
        <PublicPollPage poll={publicPoll} loading={publicPollLoading} error={publicPollError} />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      {error && 
      
      <div className="error-banner" role="status" aria-live="polite">
          <span>
           {error}
          </span>
          <button
            type="button"
            className="error-close"
            aria-label="Close message"
            onClick={() => setError('')}
          >
            ×
          </button>
        </div>
      
      }

      {infoMessage && (
        <div className="info-banner" role="status" aria-live="polite">
          <span>{infoMessage}</span>
          <button
            type="button"
            className="info-close"
            aria-label="Close message"
            onClick={() => setInfoMessage('')}
          >
            ×
          </button>
        </div>
      )}

      {isLoading && (
        <div className="loading-overlay" role="status" aria-live="polite">
          <div className="spinner" aria-hidden="true" />
          <span>Loading your workspace…</span>
        </div>
      )}

      {topUpModalOpen && (
        <TopUpModal
          onClose={() => setTopUpModalOpen(false)}
          onTopUpFree={handleTopUpFree}
          stripeConfig={stripeConfig.enabled && user ? {
            enabled: true,
            publishableKey: stripeConfig.publishableKey,
            buyButtonId: stripeConfig.buyButtonId,
            clientReferenceId: String(user.id),
            customerEmail: user.email || ''
          } : { enabled: false }}
        />
      )}

      {!token && <FeatureCarousel />}

      {voteDetailsPollId && (
        <VoteDetailsModal
          poll={polls.find((p) => p.id === voteDetailsPollId)}
          entries={voteDetails[voteDetailsPollId] || []}
          onClose={() => setVoteDetailsPollId(null)}
        />
      )}

      {!token && (
        <section>          <h2>Sign up for free / Log In</h2>
          {step === 'email' ? (
            <form onSubmit={requestCode}>
              <input
                type="email"
                placeholder="Enter your email to get code. No password required."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={MAX_NAME_LENGTH}
              />
              <button type="submit">Send Verification Code</button>
            </form>
          ) : (
            <form onSubmit={verifyCode}>
              <input
                type="text"
                placeholder="Enter verification code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              required
              maxLength={MAX_NAME_LENGTH}
              />
              <button type="submit">Verify Code</button>
            </form>
          )}
        </section>
      )}

      {token && !isLoading && user && (
        <div className="auth-info">
          <span>{user?.email}</span>
          <button
            type="button"
            className="credit-pill"
            onClick={() => setTopUpModalOpen(true)}
            aria-haspopup="dialog"
          >
            <span className="credit-pill-plus">⊕</span>Credits: {user?.credits ?? 0}
          </button>
          <button onClick={signOut} className="secondary">Sign Out</button>
        </div>
      )}

      {!token && (
        <div className="ai-tip-card">
          <em>You may prompt any AI Chat Tool to read https://www.poll-sim.com/SKILL.md to help you using poll-sim.</em>
        </div>
      )}

      {token && !isLoading && user && (
        <section>
          <div className="section-header">
            <h2>Poll Scenarios</h2>
            <button
              type="button"
              className="icon-button icon-button--create-poll"
              onClick={() => setPollFormOpen((open) => !open)}
              aria-label={pollFormOpen ? 'Hide form' : 'Show form'}
            >
              {pollFormOpen ? '−' : '+'}
            </button>
          </div>
          {pollFormOpen && (
            <form onSubmit={handleCreatePoll}>
              {polls.filter((poll) => poll.pollType === 'Scenario').length === 0 && (
                <button
                  type="button"
                  className="secondary prefill-first-time-button"
                  onClick={handlePrefillFirstTimeScenario}
                >
                  prefill anything for me to taste as this is my first time
                </button>
              )}
              <div className="form-grid">
                <label>
                  <span>Public Figure</span>
                  <select value={pollPersona} onChange={(e) => setPollPersona(e.target.value)}>
                    <option value="">Select who you are</option>
                    {personas.filter((p) => !p.archived).map((persona) => (
                      <option key={persona.id} value={persona.id}>
                        {persona.personaName}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Audience</span>
                  <select value={pollAudience} onChange={(e) => setPollAudience(e.target.value)}>
                    <option value="">Select audience</option>
                    {audiences.filter((a) => !a.archived).map((audience) => (
                      <option key={audience.id} value={audience.id}>
                        {audience.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <textarea
                placeholder="Describe what action, speech or statement the public figure makes (has made or planning to make) before the Audience's reaction can be simulated by AI."
                value={pollAction}
                onChange={(e) => setPollAction(e.target.value)}
                maxLength={MAX_PROMPT_LENGTH}
              />
              <label>
                <span>Need ideas? Try preset actions: </span>
                <select value={pollActionPreset} onChange={handlePollActionPresetSelect}>
                  <option value="">Select a preset</option>
                  {pollActionPresetOptions.map((preset) => (
                    <option key={preset} value={preset}>
                      {preset}
                    </option>
                  ))}
                </select>
              </label>
              <button type="submit">Create Poll Scenario</button>
            </form>
          )}
          <div>
            {polls.filter((poll) => poll.pollType === 'Scenario').length === 0 && !pollFormOpen && (
              <button
                type="button"
                className="empty-state-create-poll-button"
                onClick={() => setPollFormOpen((open) => !open)}
              >
                No polls yet. ADD ⊕ your first poll.
              </button>
            )}
            {polls
              .filter((poll) => poll.pollType === 'Scenario')
              .map((poll) => (
              <PollCard
                key={poll.id}
                poll={poll}
                startExpanded={poll.id === newlyCreatedPollId}
                expandRequestNonce={pollExpandRequest.pollId === poll.id ? pollExpandRequest.nonce : 0}
                onSimulate={handleSimulatePoll}
                simulatingPollId={simulatingPollId}
                simulationProgress={simulationProgress[poll.id]}
                simulationSteps={SIMULATION_STEPS}
                pollResults={pollResults}
                onToggleOptions={handleToggleOptions}
                voteDetails={voteDetails[poll.id]}
                onArchive={archivePoll}
                onShowDetails={async (pollId) => {
                  try {
                    if (!voteDetails[pollId]) {
                      await fetchVoteDetails(pollId);
                    }
                    setVoteDetailsPollId(pollId);
                  } catch (err) {
                    console.error('Failed to fetch vote details', err);
                    setError('Failed to load vote details');
                  }
                }}
              />
            ))}
          </div>
        </section>
      )}

      {token && !isLoading && user && (
        <div className="other-poll-types-toggle">
          <button type="button" className="secondary" onClick={() => setShowOtherPollTypes((prev) => !prev)}>
            {showOtherPollTypes ? 'Hide Custom Poll Types' : 'Show Custom Poll Types'}
          </button>
        </div>
      )}

      {token && showOtherPollTypes && !isLoading && user && (
        <section>
          <div className="section-header">
            <h2>Poll Opinions</h2>
            <button
              type="button"
              className="icon-button"
              onClick={() => setOpinionsPollFormOpen((open) => !open)}
              aria-label={opinionsPollFormOpen ? 'Hide form' : 'Show form'}
            >
              {opinionsPollFormOpen ? '−' : '+'}
            </button>
          </div>
          {opinionsPollFormOpen && (
            <form onSubmit={handleCreateOpinionsPoll}>
              <div className="form-grid">
                <label>
                  <span>Audience</span>
                  <select value={opinionsPollAudience} onChange={(e) => setOpinionsPollAudience(e.target.value)}>
                    <option value="">Select audience</option>
                    {audiences.filter((a) => !a.archived).map((audience) => (
                      <option key={audience.id} value={audience.id}>
                        {audience.name}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <textarea
                placeholder="Enter your poll question"
                value={opinionsPollQuestion}
                onChange={(e) => setOpinionsPollQuestion(e.target.value)}
                maxLength={MAX_PROMPT_LENGTH}
              />
              <div className="options-builder">
                <span className="options-builder-label">Options ({opinionsPollOptions.length}/{MAX_OPINIONS_OPTIONS})</span>
                {opinionsPollOptions.map((opt, index) => (
                  <div key={index} className="option-row">
                    <input
                      type="text"
                      placeholder={`Option ${index + 1}`}
                      value={opt}
                      onChange={(e) => {
                        const updated = [...opinionsPollOptions];
                        updated[index] = e.target.value;
                        setOpinionsPollOptions(updated);
                      }}
                      maxLength={MAX_NAME_LENGTH}
                    />
                    {opinionsPollOptions.length > 2 && (
                      <button
                        type="button"
                        className="icon-button secondary"
                        onClick={() => setOpinionsPollOptions(opinionsPollOptions.filter((_, i) => i !== index))}
                        aria-label="Remove option"
                      >
                        −
                      </button>
                    )}
                  </div>
                ))}
                {opinionsPollOptions.length < MAX_OPINIONS_OPTIONS && (
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => setOpinionsPollOptions([...opinionsPollOptions, ''])}
                  >
                    + Add Option
                  </button>
                )}
              </div>
              <button type="submit">Create Poll Opinion</button>
            </form>
          )}
          <div>
            {polls.filter((poll) => poll.pollType === 'Opinions').length === 0 && <p>No polls created yet.</p>}
            {polls
              .filter((poll) => poll.pollType === 'Opinions')
              .map((poll) => (
                <PollCard
                  key={poll.id}
                  poll={poll}
                  startExpanded={poll.id === newlyCreatedPollId}
                  expandRequestNonce={pollExpandRequest.pollId === poll.id ? pollExpandRequest.nonce : 0}
                  onSimulate={handleSimulatePoll}
                  simulatingPollId={simulatingPollId}
                  simulationProgress={simulationProgress[poll.id]}
                  simulationSteps={SIMULATION_STEPS}
                  pollResults={pollResults}
                  onToggleOptions={handleToggleOptions}
                  voteDetails={voteDetails[poll.id]}
                  onArchive={archivePoll}
                  onShowDetails={async (pollId) => {
                    try {
                      if (!voteDetails[pollId]) {
                        await fetchVoteDetails(pollId);
                      }
                      setVoteDetailsPollId(pollId);
                    } catch (err) {
                      console.error('Failed to fetch vote details', err);
                      setError('Failed to load vote details');
                    }
                  }}
                />
              ))}
          </div>
        </section>
      )}

      {token && !isLoading && user && (
        <section>
          <div className="section-header">
            <h2>Public Figures</h2>
            <button
              type="button"
              className="icon-button"
              onClick={() => setPersonaFormOpen((open) => !open)}
              aria-label={personaFormOpen ? 'Hide form' : 'Show form'}
            >
              {personaFormOpen ? '−' : '+'}
            </button>
          </div>
          {personaFormOpen && (
            <form onSubmit={handleCreatePersona}>
              <input
                type="text"
                placeholder="Public Figure name"
                value={personaName}
                onChange={(e) => setPersonaName(e.target.value)}
                maxLength={MAX_NAME_LENGTH}
              />
              <textarea
                placeholder="Describe the objective overall info about the public figure, such as their bio profile, background, role, career, social positions and statuses. (It is a good idea to ask your AI chat to write it for you within 300 words.)"
                value={personaPrompt}
                onChange={(e) => setPersonaPrompt(e.target.value)}
                maxLength={MAX_PROMPT_LENGTH}
              />
              <button type="submit">Create Public Figure</button>
            </form>
          )}
          <ul>
            {personas.map((persona) => (
              <PersonaItem key={persona.id} persona={persona} onUpdatePrompt={updatePersonaPrompt} onArchive={archivePersona} />
            ))}
          </ul>
        </section>
      )}

      {token && !isLoading && user && isAdmin && (
        <section>
          <div className="section-header">
            <h2>Dashboard</h2>
          </div>
          {dashboardStats.loading ? (
            <p>Loading dashboard metrics...</p>
          ) : dashboardStats.error ? (
            <p>{dashboardStats.error}</p>
          ) : (
            <div className="dashboard-stats-grid">
              <div className="dashboard-stat-card">
                <h3>New Users</h3>
                <strong>{dashboardStats.newUsers}</strong>
              </div>
              <div className="dashboard-stat-card">
                <h3>New Polls</h3>
                <strong>{dashboardStats.newPolls}</strong>
              </div>
              <div className="dashboard-stat-card">
                <h3>New Simulations</h3>
                <strong>{Math.floor((dashboardStats.newVotes/10.0)+0.5)}</strong>
              </div>
              <div className="dashboard-stat-card">
                <h3>New Shares</h3>
                <strong>{dashboardStats.newShares}</strong>
              </div>
            </div>
          )}
        </section>
      )}

      {token && !isLoading && user && (
        <div className="advanced-toggle-row">
          <button type="button" className="secondary" onClick={() => setShowAdvanced((open) => !open)}>
            {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
          </button>
        </div>
      )}

      {token && showAdvanced && !isLoading && user && (
        <section>
          <div className="section-header">
            <h2>Audience</h2>
            <button
              type="button"
              className="icon-button"
              onClick={() => setAudienceFormOpen((open) => !open)}
              aria-label={audienceFormOpen ? 'Hide form' : 'Show form'}
            >
              {audienceFormOpen ? '−' : '+'}
            </button>
          </div>
          {audienceFormOpen && (
            <form onSubmit={handleCreateAudience}>
              <input
                type="text"
                placeholder="Audience name"
                value={audienceName}
                onChange={(e) => setAudienceName(e.target.value)}
                maxLength={MAX_NAME_LENGTH}
              />
              <textarea
                placeholder="Describe how this audience or society behaves generally, including culture, attitudes, and social context. (demographics can be specified with Groups) - It is a good idea to ask your AI chat to write it for you within 300 words."
                value={audiencePrompt}
                onChange={(e) => setAudiencePrompt(e.target.value)}
                maxLength={MAX_PROMPT_LENGTH}
              />
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={audiencePublic}
                  onChange={(e) => setAudiencePublic(e.target.checked)}
                  disabled={!isAdmin}
                />
                <span>Make public for all users {isAdmin ? '' : '(Admins only)'}</span>
              </label>
              <button type="submit">Create Audience</button>
            </form>
          )}
          <ul>
            {audiences.map((audience) => (
              <AudienceItem
                key={audience.id}
                audience={audience}
                groups={groups}
                fetchAudienceGroups={fetchAudienceGroups}
                linkGroupToAudience={linkGroupToAudience}
                unlinkGroupFromAudience={unlinkGroupFromAudience}
                currentUserId={user?.id ?? null}
                isAdmin={isAdmin}
                onUpdatePrompt={updateAudiencePrompt}
                onArchive={archiveAudience}
              />
            ))}
          </ul>
        </section>
      )}

      {token && showAdvanced && !isLoading && user && (
        <section>
          <div className="section-header">
            <h2>Groups</h2>
            <button
              type="button"
              className="icon-button"
              onClick={() => setGroupFormOpen((open) => !open)}
              aria-label={groupFormOpen ? 'Hide form' : 'Show form'}
            >
              {groupFormOpen ? '−' : '+'}
            </button>
          </div>
          {groupFormOpen && (
            <form onSubmit={handleCreateGroup}>
              <input
                type="text"
                placeholder="Group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                maxLength={MAX_NAME_LENGTH}
              />
              <textarea
                placeholder="Describe this group's or class's traits, viewpoints, and lifestyle. (It is a good idea to ask your AI chat to write it for you within 300 words.) "
                value={groupPrompt}
                onChange={(e) => setGroupPrompt(e.target.value)}
                maxLength={MAX_PROMPT_LENGTH}
              />
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={groupPublic}
                  onChange={(e) => setGroupPublic(e.target.checked)}
                  disabled={!isAdmin}
                />
                <span>Make public for all users {isAdmin ? '' : '(Admins only)'}</span>
              </label>
              <button type="submit">Create Group</button>
            </form>
          )}
          <ul>
            {groups.map((group) => (
              <GroupItem
                key={group.id}
                group={group}
                currentUserId={user?.id ?? null}
                isAdmin={isAdmin}
                onUpdatePrompt={updateGroupPrompt}
                onArchive={archiveGroup}
              />
            ))}
          </ul>
        </section>
      )}

       {token && !isLoading && user && showAiHelpBanner && (
        <div className="authenticated-alert-banner" role="status" aria-live="polite">

          <span>
            {Math.random() < 0.5
              ? 'Run out of credits? Top up daily for free or pay only AU$10 for 50 credits.'
              : 'You may prompt your AI chat tool: "read https://www.poll-sim.com/SKILL.md to help me using poll-sim." '}
          </span>
          <button
            type="button"
            className="authenticated-alert-close"
            aria-label="Close message"
            onClick={() => setShowAiHelpBanner(false)}
          >
            ×
          </button>
        </div>
      )}

      <footer className="footer-social">
        <SocialIconRow />
      </footer>
    </div>
  );
}

const FeatureCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % FEATURE_SLIDES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="feature-carousel" aria-label="Product feature highlights">
      {FEATURE_SLIDES.map((slide, index) => (
        <div
          key={slide.id}
          className={`carousel-slide-content ${index === activeIndex ? 'active' : ''}`}
          role="group"
          aria-roledescription="slide"
          aria-hidden={index !== activeIndex}
        >
          {slide.type === 'image' ? (
            <img src={slide.src} alt={slide.alt} className="carousel-slide-image" />
          ) : (
            <>
              <p className="carousel-tagline">Scenario Intelligence</p>
              <h1>{slide.headline}</h1>
            </>
          )}
        </div>
      ))}
      <div className="carousel-indicators" role="tablist">
        {FEATURE_SLIDES.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            className={`carousel-indicator ${index === activeIndex ? 'active' : ''}`}
            onClick={() => setActiveIndex(index)}
            aria-label={`Show slide ${index + 1}`}
            aria-pressed={index === activeIndex}
          />
        ))}
      </div>
    </div>
  );
};

const SocialIconRow = () => (
  <div className="social-links-wrap">
    <div className="social-icons" aria-label="Connect with us">
      {BRAND_ICONS.filter((item) => !['producthunt', 'shipit','aiDir'].includes(item.id)).map((item) => {
        const Element = item.href ? 'a' : 'button';
        const elementProps = item.href
          ? {
              href: item.href,
              ...(item.target ? { target: item.target } : {}),
              ...(item.rel ? { rel: item.rel } : {})
            }
          : { type: 'button' };
        return (
          <Element
            key={item.id}
            className={`social-icon social-icon--${item.id}`}
            aria-label={item.label}
            {...elementProps}
          >
            {renderIcon(item.icon)}
          </Element>
        );
      })}
    </div>
    <div className="social-badges" aria-label="Featured on">
      {BRAND_ICONS.filter((item) => ['producthunt', 'shipit','aiDir'].includes(item.id)).map((item) => {
        const Element = item.href ? 'a' : 'button';
        const elementProps = item.href
          ? {
              href: item.href,
              ...(item.target ? { target: item.target } : {}),
              ...(item.rel ? { rel: item.rel } : {})
            }
          : { type: 'button' };
        return (
          <Element
            key={item.id}
            className={`social-icon social-icon--${item.id}`}
            aria-label={item.label}
            {...elementProps}
          >
            {renderIcon(item.icon)}
          </Element>
        );
      })}
    </div>
  </div>
);

const renderIcon = (type) => {
  switch (type) {
    case 'productHuntBadge':
      return (
        <img
          src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1123182&theme=light&t=1776231914178"
          alt="Poll Sim - Predict Audience Reaction with AI | Product Hunt"
          className="social-icon-badge"
        />
      );
    case 'shipitBadge':
      return (
        <img
          src="https://www.shipit.buzz/api/products/poll-simulator/badge?theme=light"
          alt="Featured on Shipit"
          className="social-icon-badge"
        />
      );
     case 'aiDirBadge':
      return (
        <img
          src="https://aiagentsdirectory.com/featured-badge.svg?v=2024"
          alt="Featured on AI Agents Directory"
          className="social-icon-badge"
        />
      );
    case 'mdiDiscord':
      return (
        <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path d="M20.317 4.369A18.227 18.227 0 0 0 15.892 3l-.218.447c1.21.339 2.348.84 3.395 1.488-1.455-.686-2.868-1.029-4.239-1.029-.367 0-.732.025-1.094.075a18.15 18.15 0 0 0-4.239 1.029c1.047-.648 2.183-1.149 3.395-1.488L12.475 3a18.227 18.227 0 0 0-4.425 1.369C3.912 8.226 2.901 12 3.101 15.715A18.404 18.404 0 0 0 7.22 17l.516-1.004c-.282-.104-.555-.228-.817-.37l.187-.134c1.58.73 3.285 1.124 5.093 1.124s3.513-.393 5.093-1.124l.187.134a7.7 7.7 0 0 1-.818.37L17.28 17a18.4 18.4 0 0 0 4.118-1.285c.342-5.488-1.093-9.238-1.081-11.346Zm-11.07 8.04c-.978 0-1.774-.89-1.774-1.983 0-1.092.782-1.978 1.774-1.978 1 0 1.795.895 1.774 1.978 0 1.093-.782 1.983-1.774 1.983Zm5.51 0c-.978 0-1.774-.89-1.774-1.983 0-1.092.782-1.978 1.774-1.978 1 0 1.795.895 1.774 1.978 0 1.093-.774 1.983-1.774 1.983Z" />
        </svg>
      );
    case 'mdiFacebook':
      return (
        <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path d="M22 12a10 10 0 1 0-11.5 9.87v-6.99H8.4V12h2.1V9.8c0-2.07 1.23-3.22 3.12-3.22.9 0 1.84.16 1.84.16v2.03h-1.04c-1.02 0-1.34.63-1.34 1.28V12h2.28l-.36 2.88h-1.92v6.99C18.83 21.07 22 16.92 22 12Z" />
        </svg>
      );
    case 'mdiBluesky':
      return (
        <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.756 3.818 3.672 6.558 3.048-3.685.535-6.192 2.381-3.616 4.233 4.328 3.051 6.587 1.197 7.81-1.134H12c1.223 2.331 3.482 4.185 7.81 1.134 2.576-1.852.07-3.698-3.616-4.233 2.74.341 5.743-.292 6.559-3.048C22.622 9.418 24 4.458 24 3.768c0-.689-.138-1.861-.902-2.203-.659-.299-1.664-.621-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" />
        </svg>
      );
    case 'mdiX':
      return (
        <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path d="m18.36 3 2.85 2.85-5.57 5.57 5.57 5.58-2.85 2.85-5.58-5.57-5.57 5.57L4.36 17l5.57-5.58L4.36 5.85 7.21 3l5.57 5.57L18.36 3Z" />
        </svg>
      );
    case 'mdiEmail':
      return (
        <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path d="M20 4H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h16a2 2 0 0 0 2-2V6c0-1.1-.9-2-2-2Zm0 4-8 5-8-5V6l8 5 8-5v2Z" />
        </svg>
      );
    case 'mdiHelpCircle':
      return (
        <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 17a1.25 1.25 0 1 1 1.25-1.25A1.25 1.25 0 0 1 12 19Zm1.29-5.5-.71.36a1 1 0 0 0-.58.9v.24h-2v-.24a3 3 0 0 1 1.7-2.7l.71-.36A1.73 1.73 0 1 0 10 10H8a3.73 3.73 0 1 1 5.29 3.5Z" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path d="M20 4H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h16a2 2 0 0 0 2-2V6c0-1.1-.9-2-2-2Zm0 4-8 5-8-5V6l8 5 8-5v2Z" />
        </svg>
      );
  }
};

const PersonaItem = ({ persona, onUpdatePrompt, onArchive }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState(persona.aiPrompt);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState('');

  useEffect(() => {
    setDraftPrompt(persona.aiPrompt);
  }, [persona.aiPrompt]);

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);
    onUpdatePrompt(persona.id, draftPrompt)
      .then(() => {
        setEditError('');
        setEditing(false);
      })
      .catch(() => setEditError('Failed to update public figure prompt.'))
      .finally(() => setSaving(false));
  };

  const handleArchive = () => {
    if (!window.confirm('Archive this public figure? This cannot be undone.')) return;
    setArchiving(true);
    onArchive(persona.id)
      .catch(() => {
        setArchiveError('Failed to archive public figure.');
        setArchiving(false);
      });
  };

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (!next) {
        setEditing(false);
      }
      return next;
    });
  };

  return (
    <li>
      <div className="persona-row">
        <div>
          <strong>{persona.personaName}</strong>
        </div>
        <div className="row-actions">
          <button
            type="button"
            className="icon-button secondary"
            onClick={toggleOpen}
            aria-label={open ? 'Hide description' : 'Show description'}
          >
            {open ? '−' : <ToggleChevronIcon />}
          </button>
          {open && (
            <button type="button" className="secondary" onClick={() => setEditing((prev) => !prev)}>
              {editing ? 'Close Edit' : 'Edit'}
            </button>
          )}
          <button type="button" className="icon-button secondary archive-button" onClick={handleArchive} disabled={archiving}>
            {archiving ? (
              '…'
            ) : (
              <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
                <path d="M20.54 5.23 19.15 3.55A1.98 1.98 0 0 0 17.61 3H6.4c-.58 0-1.13.25-1.52.68L3.46 5.23c-.29.35-.46.8-.46 1.27V19a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.5c0-.47-.17-.92-.46-1.27ZM12 17l-4-4h2.5v-3h3v3H16l-4 4ZM5.12 6l1.2-1.45h11.37L18.88 6H5.12Z" />
              </svg>
            )}
          </button>
        </div>
      </div>
      {archiveError && <div className="error compact">{archiveError}</div>}
      {open && <p className="persona-description">{persona.aiPrompt}</p>}
      {editing && open && (
        <form className="inline-form" onSubmit={handleSave}>
          <textarea value={draftPrompt} onChange={(e) => setDraftPrompt(e.target.value)} maxLength={MAX_PROMPT_LENGTH} />
          {editError && <div className="error compact">{editError}</div>}
          <div className="row-actions">
            <button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button type="button" className="secondary" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </li>
  );
};

const AudienceItem = ({
  audience,
  groups,
  fetchAudienceGroups,
  linkGroupToAudience,
  unlinkGroupFromAudience,
  currentUserId,
  isAdmin,
  onUpdatePrompt,
  onArchive
}) => {
  const [open, setOpen] = useState(false);
  const [linkFormOpen, setLinkFormOpen] = useState(false);
  const [linkedGroups, setLinkedGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [percentage, setPercentage] = useState('');
  const [linkError, setLinkError] = useState('');
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [promptDraft, setPromptDraft] = useState(audience.aiPrompt);
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptError, setPromptError] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState('');

  useEffect(() => {
    setPromptDraft(audience.aiPrompt);
  }, [audience.aiPrompt]);

  useEffect(() => {
    setLinkedGroups((prev) =>
      prev.map((link) => {
        const updated = groups.find((group) => group.id === link.groupId);
        return updated
          ? {
              ...link,
              name: updated.name,
              aiPrompt: updated.aiPrompt,
              isPublic: updated.isPublic,
              userId: updated.userId
            }
          : link;
      })
    );
  }, [groups]);

  const isPublic = Boolean(audience.isPublic);
  const ownsAudience = currentUserId != null && audience.userId === currentUserId;
  const canEditPrompt = (!isPublic && ownsAudience) || (isPublic && isAdmin);
  const canArchive = (ownsAudience && !isPublic) || (isPublic && isAdmin);

  const loadLinks = () => {
    setLoadingLinks(true);
    fetchAudienceGroups(audience.id)
      .then((data) => {
        setLinkedGroups(data);
        setLinkError('');
      })
      .catch(() => setLinkError('Failed to load groups for this audience.'))
      .finally(() => setLoadingLinks(false));
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (!open && linkedGroups.length === 0) {
      loadLinks();
    }
    if (!next) {
      setEditingPrompt(false);
    }
  };

  const handleLink = (e) => {
    e.preventDefault();
    if (!selectedGroup || percentage === '') {
      setLinkError('Select a group and specify percentage.');
      return;
    }
    linkGroupToAudience(audience.id, {
      groupId: Number(selectedGroup),
      percentage: Number(percentage)
    })
      .then(() => {
        setLinkError('');
        setSelectedGroup('');
        setPercentage('');
        setLinkFormOpen(false);
        loadLinks();
      })
      .catch(() => setLinkError('Failed to link group.'));
  };

  const handlePromptSave = (e) => {
    e.preventDefault();
    setPromptSaving(true);
    onUpdatePrompt(audience.id, promptDraft)
      .then(() => {
        setPromptError('');
        setEditingPrompt(false);
      })
      .catch(() => setPromptError('Failed to update audience prompt.'))
      .finally(() => setPromptSaving(false));
  };

  const canDeleteLinks = (!isPublic && ownsAudience) || (isPublic && isAdmin);

  const handleArchive = () => {
    if (!window.confirm('Archive this audience? This cannot be undone.')) return;
    setArchiving(true);
    onArchive(audience.id)
      .catch(() => {
        setArchiveError('Failed to archive audience.');
        setArchiving(false);
      });
  };

  const handleUnlink = (groupId) => {
    unlinkGroupFromAudience(audience.id, groupId)
      .then(() => {
        setLinkedGroups((prev) => prev.filter((link) => link.groupId !== groupId));
      })
      .catch(() => setLinkError('Failed to remove group from audience.'));
  };

  return (
    <li>
      <div className="persona-row">
        <div>
          <strong>{audience.name}</strong>
          {audience.isPublic ? <span className="badge">Public</span> : null}
        </div>
        <div className="row-actions">
          <button
            type="button"
            className="icon-button secondary"
            onClick={toggleOpen}
            aria-label={open ? 'Hide audience details' : 'Show audience details'}
          >
            {open ? '−' : <ToggleChevronIcon />}
          </button>
          {open && canEditPrompt && (
            <button type="button" className="secondary" onClick={() => setEditingPrompt((state) => !state)}>
              {editingPrompt ? 'Close Edit' : 'Edit'}
            </button>
          )}
          {canArchive && (
            <button type="button" className="icon-button secondary archive-button" onClick={handleArchive} disabled={archiving}>
              {archiving ? (
                '…'
              ) : (
                <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
                  <path d="M20.54 5.23 19.15 3.55A1.98 1.98 0 0 0 17.61 3H6.4c-.58 0-1.13.25-1.52.68L3.46 5.23c-.29.35-.46.8-.46 1.27V19a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.5c0-.47-.17-.92-.46-1.27ZM12 17l-4-4h2.5v-3h3v3H16l-4 4ZM5.12 6l1.2-1.45h11.37L18.88 6H5.12Z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
      {archiveError && <div className="error compact">{archiveError}</div>}
      {open && (
        <div className="audience-detail">
          <p className="persona-description">{audience.aiPrompt}</p>
          {editingPrompt && canEditPrompt && (
            <form className="inline-form" onSubmit={handlePromptSave}>
              <textarea value={promptDraft} onChange={(e) => setPromptDraft(e.target.value)} maxLength={MAX_PROMPT_LENGTH} />
              {promptError && <div className="error compact">{promptError}</div>}
              <div className="row-actions">
                <button type="submit" disabled={promptSaving}>
                  {promptSaving ? 'Saving…' : 'Save Changes'}
                </button>
                <button type="button" className="secondary" onClick={() => setEditingPrompt(false)} disabled={promptSaving}>
                  Cancel
                </button>
              </div>
            </form>
          )}
          <div className="section-subheader">
            <h4>Groups</h4>
            {canDeleteLinks && (
              <button
                type="button"
                className="icon-button"
                onClick={() => setLinkFormOpen((state) => !state)}
                aria-label={linkFormOpen ? 'Hide link form' : 'Show link form'}
              >
                {linkFormOpen ? '−' : '+'}
              </button>
            )}
          </div>
          {linkFormOpen && (
            <form onSubmit={handleLink}>
              <select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)}>
                <option value="">Select group</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name} {group.isPublic ? '(Public)' : ''}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="Demographic %"
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
              />
              <button type="submit">Link Group</button>
            </form>
          )}
          {linkError && <div className="error compact">{linkError}</div>}
          {loadingLinks ? (
            <p>Loading linked groups...</p>
          ) : (
            <ul className="linked-groups">
              {linkedGroups.map((link) => (
                <li key={`${audience.id}-${link.groupId}`}>
                  <div className="persona-row">
                    <div>
                      <strong>{link.name}</strong>
                      {link.isPublic ? <span className="badge">Public</span> : null}
                    </div>
                    <div className="row-actions">
                      <span className="percentage-chip">{Number(link.percentage).toFixed(1)}%</span>
                      {canDeleteLinks && (
                        <button type="button" className="secondary" onClick={() => handleUnlink(link.groupId)}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
};

const GroupItem = ({ group, currentUserId, isAdmin, onUpdatePrompt, onArchive }) => {
  const [open, setOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [promptDraft, setPromptDraft] = useState(group.aiPrompt);
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptError, setPromptError] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState('');

  useEffect(() => {
    setPromptDraft(group.aiPrompt);
  }, [group.aiPrompt]);

  const isPublic = Boolean(group.isPublic);
  const ownsGroup = currentUserId != null && group.userId === currentUserId;
  const canEditPrompt = (!isPublic && ownsGroup) || (isPublic && isAdmin);
  const canArchive = (ownsGroup && !isPublic) || (isPublic && isAdmin);

  const handlePromptSave = (e) => {
    e.preventDefault();
    setPromptSaving(true);
    onUpdatePrompt(group.id, promptDraft)
      .then(() => {
        setPromptError('');
        setEditingPrompt(false);
      })
      .catch(() => setPromptError('Failed to update group prompt.'))
      .finally(() => setPromptSaving(false));
  };

  const handleArchive = () => {
    if (!window.confirm('Archive this group? This cannot be undone.')) return;
    setArchiving(true);
    onArchive(group.id)
      .catch(() => {
        setArchiveError('Failed to archive group.');
        setArchiving(false);
      });
  };

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (!next) {
        setEditingPrompt(false);
      }
      return next;
    });
  };

  return (
    <li>
      <div className="persona-row">
        <div>
          <strong>{group.name}</strong>
          {group.isPublic ? <span className="badge">Public</span> : null}
        </div>
        <div className="row-actions">
          <button
            type="button"
            className="icon-button secondary"
            onClick={toggleOpen}
            aria-label={open ? 'Hide group description' : 'Show group description'}
          >
            {open ? '−' : <ToggleChevronIcon />}
          </button>
          {open && canEditPrompt && (
            <button type="button" className="secondary" onClick={() => setEditingPrompt((state) => !state)}>
              {editingPrompt ? 'Close Edit' : 'Edit'}
            </button>
          )}
          {canArchive && (
            <button type="button" className="icon-button secondary archive-button" onClick={handleArchive} disabled={archiving}>
              {archiving ? (
                '…'
              ) : (
                <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
                  <path d="M20.54 5.23 19.15 3.55A1.98 1.98 0 0 0 17.61 3H6.4c-.58 0-1.13.25-1.52.68L3.46 5.23c-.29.35-.46.8-.46 1.27V19a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.5c0-.47-.17-.92-.46-1.27ZM12 17l-4-4h2.5v-3h3v3H16l-4 4ZM5.12 6l1.2-1.45h11.37L18.88 6H5.12Z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>
      {archiveError && <div className="error compact">{archiveError}</div>}
      {open && <p className="persona-description">{group.aiPrompt}</p>}
      {editingPrompt && open && canEditPrompt && (
        <form className="inline-form" onSubmit={handlePromptSave}>
          <textarea value={promptDraft} onChange={(e) => setPromptDraft(e.target.value)} maxLength={MAX_PROMPT_LENGTH} />
          {promptError && <div className="error compact">{promptError}</div>}
          <div className="row-actions">
            <button type="submit" disabled={promptSaving}>
              {promptSaving ? 'Saving…' : 'Save Changes'}
            </button>
            <button type="button" className="secondary" onClick={() => setEditingPrompt(false)} disabled={promptSaving}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </li>
  );
};

const PollCard = ({ poll, startExpanded = false, expandRequestNonce = 0, onSimulate, simulatingPollId, simulationProgress = 0, simulationSteps = 10, pollResults, onToggleOptions, voteDetails, onShowDetails, onArchive }) => {
  const [showOptions, setShowOptions] = useState(startExpanded);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState('');
  const [shareStatus, setShareStatus] = useState('');
  const createdDate = formatPollCreatedDate(poll.createdAt);
  const isSimulating = simulatingPollId === poll.id;

  useEffect(() => {
    if (startExpanded) {
      onToggleOptions?.(poll.id, true);
    }
  }, [onToggleOptions, poll.id, startExpanded]);

  useEffect(() => {
    if (!expandRequestNonce) {
      return;
    }
    setShowOptions(true);
    onToggleOptions?.(poll.id, true);
  }, [expandRequestNonce, onToggleOptions, poll.id]);

  const handleArchive = () => {
    if (!window.confirm('Archive this poll? This cannot be undone.')) return;
    setArchiving(true);
    onArchive(poll.id)
      .catch(() => {
        setArchiveError('Failed to archive poll.');
        setArchiving(false);
      });
  };

  const handleShare = async () => {
    if (!poll.shareToken) {
      setShareStatus('Share link unavailable');
      return;
    }
    const shareUrl = `${window.location.origin}/?share=${encodeURIComponent(poll.shareToken)}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const temp = document.createElement('textarea');
        temp.value = shareUrl;
        temp.setAttribute('readonly', '');
        temp.style.position = 'absolute';
        temp.style.left = '-9999px';
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      setShareStatus('Copied share link');
    } catch (_error) {
      setShareStatus('Unable to copy link');
    }
    window.setTimeout(() => setShareStatus(''), 2500);
  };

  return (
    <div className="poll-card">
      <div className="poll-header">
        {poll.personaName ? <h3>{poll.personaName}</h3> : null}
        <div className="poll-header-meta">
          <span className="poll-meta">{createdDate}</span>
          <button type="button" className="cta-button" onClick={() => onSimulate(poll)} disabled={isSimulating}>
            {isSimulating ? `Simulating… (${simulationProgress}/${simulationSteps})` : 'Request AI to Simulate Answers'}
          </button>
        </div>
      </div>
      <p className="poll-meta">Audience: {poll.audienceName}</p>
      <p className="persona-description">{poll.question}</p>
      <div className="row-actions">
        <button
          type="button"
          className="icon-button secondary"
          onClick={() => {
            setShowOptions((state) => {
              const next = !state;
              onToggleOptions?.(poll.id, next);
              return next;
            });
          }}
          aria-label={showOptions ? 'Hide options' : 'Show options'}
        >
          {showOptions ? '−' : <ToggleChevronIcon />}
        </button>
        <button
          type="button"
          className="icon-button secondary share-icon-button"
          onClick={handleShare}
          aria-label="Copy share link"
          title="Copy share link"
        >
          <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
            <path d="M18 16a3 3 0 0 0-2.24 1.03l-6.14-3.35a3.12 3.12 0 0 0 0-1.36l6.14-3.35A3 3 0 1 0 15 7a2.93 2.93 0 0 0 .08.69L8.94 11a3 3 0 1 0 0 2l6.14 3.31A2.93 2.93 0 0 0 15 17a3 3 0 1 0 3-3Z" />
          </svg>
        </button>
        <button type="button" className="icon-button secondary archive-button" onClick={handleArchive} disabled={archiving || isSimulating}>
          {archiving ? (
            '…'
          ) : (
            <svg viewBox="0 0 24 24" role="img" aria-hidden="true">
              <path d="M20.54 5.23 19.15 3.55A1.98 1.98 0 0 0 17.61 3H6.4c-.58 0-1.13.25-1.52.68L3.46 5.23c-.29.35-.46.8-.46 1.27V19a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.5c0-.47-.17-.92-.46-1.27ZM12 17l-4-4h2.5v-3h3v3H16l-4 4ZM5.12 6l1.2-1.45h11.37L18.88 6H5.12Z" />
            </svg>
          )}
        </button>
        <span className="poll-view-count" aria-label={`View count ${Number(poll.viewCount) || 0}`}>
          {Number(poll.viewCount) || 0} views
        </span>
      </div>
      {archiveError && <div className="error compact">{archiveError}</div>}
      {shareStatus && <div className="poll-meta">{shareStatus}</div>}
      {showOptions && (
        <ul className="poll-options">
          {poll.options.map((option) => {
            const votes = pollResults?.[poll.id]?.[option.id] ?? 0;
            return (
              <li key={option.id}>
                <div className="poll-option-row">
                  <span>{option.optionText}</span>
                  <span className="poll-option-votes">{votes} vote{votes === 1 ? '' : 's'}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {showOptions && (
        <button type="button" className="secondary" onClick={() => onShowDetails?.(poll.id)}>
          View Details
        </button>
      )}
    </div>
  );
};

const PublicPollPage = ({ poll, loading, error }) => {
  if (loading) {
    return (
      <section>
        <h2>Shared Poll</h2>
        <p>Loading poll...</p>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <h2>Shared Poll</h2>
        <div className="error">{error}</div>
      </section>
    );
  }

  if (!poll) {
    return null;
  }

  const createdDate = formatPollCreatedDate(poll.createdAt);

  return (
    <section>
      <div className="public-poll-header">
        <h2>Shared Poll</h2>
        <span className="poll-meta">{createdDate}</span>
      </div>
      {poll.personaName ? <p className="poll-meta">Public Figure: {poll.personaName}</p> : null}
      <p className="poll-meta">Audience: {poll.audienceName}</p>
      <p className="persona-description">{poll.question}</p>
      <ul className="poll-options public-poll-options">
        {poll.options.map((option) => (
          <li key={option.id}>
            <div className="poll-option-row">
              <span>{option.optionText}</span>
              <span className="poll-option-votes">{option.votes} vote{option.votes === 1 ? '' : 's'}</span>
            </div>
          </li>
        ))}
      </ul>
      <div className="public-poll-cta-wrap">
        <a className="public-poll-cta-button" href="/" aria-label="Go to home and sign up or sign in">
          Get AI to Answer Your Own Polls for Free
        </a>
      </div>
    </section>
  );
};

const VoteDetailsModal = ({ poll, entries, onClose }) => {
  if (!poll) return null;
  const formatComment = (value) => {
    if (!value) return '—';
    const text = String(value).trim();
    if (text.length <= 500) return text;
    return `${text.slice(0, 500)}…`;
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="modal-panel modal-panel--compact modal-panel--vote-details">
        <div className="modal-header">
          <div>
            <h3 className="modal-heading-sm">Vote Details</h3>
            <p className="poll-meta modal-subheading">{poll.personaName ? `${poll.personaName} → ` : ''}{poll.audienceName}</p>
          </div>
          <button type="button" className="icon-button secondary" onClick={onClose} aria-label="Close details">
            ×
          </button>
        </div>
        <div className="modal-body modal-body--scroll">
          {entries.length === 0 ? (
            <p>No votes recorded yet.</p>
          ) : (
            <table className="modal-table modal-table--compact modal-table--vote-details">
              <thead>
                <tr>
                  <th>Option</th>
                  <th>Group</th>
                  <th>Comment</th>
                  <th>AI Model</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{entry.optionText}</td>
                    <td>{entry.groupName || 'N/A'}</td>
                    <td className="modal-comment">{formatComment(entry.voteComment)}</td>
                    <td>{entry.aiModel || 'Manual Vote'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const TopUpModal = ({ onClose, onTopUpFree, stripeConfig }) => (
  <div className="modal-backdrop" role="dialog" aria-modal="true">
    <div className="modal-panel">
      <div className="modal-header">
        <div>
          <h3>Top Up Credits</h3>
        </div>
        <button type="button" className="icon-button secondary" onClick={onClose} aria-label="Close top up options">
          ×
        </button>
      </div>
      <div className="modal-body topup-modal-body">
        <p>Add credits to continue running AI simulations.</p>
        <div className="topup-actions">
          <button type="button" onClick={onTopUpFree}>
            Top Up for Free <span className="topup-free-hint">2 Credits DAILY</span>
          </button>
          {stripeConfig?.enabled && (
            <stripe-buy-button
              buy-button-id={stripeConfig.buyButtonId}
              publishable-key={stripeConfig.publishableKey}
              client-reference-id={stripeConfig.clientReferenceId}
              customer-email={stripeConfig.customerEmail}
            />
          )}
        </div>
      </div>
    </div>
  </div>
);

function Header() {
  return (
    <header className="app-title">
      <img src={`${process.env.PUBLIC_URL}/logo192.png`} alt="Poll Simulator logo" className="app-title__logo" />
      <h1>Poll Simulator</h1>
      <h5>Predict Audience Support with AI</h5>
    </header>
  );
}

export default App;
